import type { Table as TableType } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import { tw } from "~/utils";
import { SortIcon } from "./SortIcon";
import { TableBody } from "./TableBody";
import { TableContent } from "./TableContent";
import { TableHead } from "./TableHead";
import { TableHeader } from "./TableHeader";
import { TableRow } from "./TableRow";
import { TableSkeleton } from "./TableSkeleton";
import { TableWrapper } from "./TableWrapper";

export interface TableProps<TData> {
  className?: string;
  table: TableType<TData>;
  isLoadingData: boolean;
  rowsPerPage: number;
  onRowClick?: (row: TData) => void;
}

/*
   Don't use directly, use SinglePageTable or PaginatedTable instead
*/

export const Table = <TData,>({
  table,
  isLoadingData,
  rowsPerPage,
  onRowClick,
  className,
}: TableProps<TData>) => {
  return (
    <TableWrapper
      className={tw("overflow-auto border border-gray-200", className)}
    >
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.column.getCanSort() ? (
                  <button
                    className="flex items-center gap-2 truncate hover:cursor-pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    <SortIcon
                      header={header}
                      className="text-indigo inline w-4 stroke-2"
                    />
                  </button>
                ) : (
                  <div className="text-left">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoadingData ? (
          <TableSkeleton
            numRows={rowsPerPage}
            columnsLength={table.getAllColumns().length}
          />
        ) : (
          <TableContent
            rows={table.getRowModel().rows}
            numColumns={table.getAllColumns().length}
            onRowClick={onRowClick}
          />
        )}
      </TableBody>
    </TableWrapper>
  );
};
Table.displayName = "Table";
